import classnames from 'classnames'
import { ReactNode } from 'react'

import { THEMES } from '../../App'
import { REVEAL_TIME_MS } from '../../constants/settings'
import { getStoredIsHighContrastMode } from '../../lib/localStorage'
import { CharStatus } from '../../lib/statuses'
import { solution } from '../../lib/words'

type Props = {
  children?: ReactNode
  value: string
  width?: number
  status?: CharStatus
  onClick: (value: string) => void
  isRevealing?: boolean
  isEnterKey?: boolean
  theme?: string
}

export const Key = ({
  children,
  status,
  width = 40,
  value,
  onClick,
  isRevealing,
  isEnterKey,
  theme,
}: Props) => {
  const keyDelayMs = REVEAL_TIME_MS * solution.length
  const isHighContrast = getStoredIsHighContrastMode()

  const classes = classnames(
    'xxshort:h-8 xxshort:w-8 xxshort:text-xxs xshort:w-10 xshort:h-10 flex short:h-12 h-14 items-center justify-center rounded mx-0.5 text-xs font-bold cursor-pointer select-none dark:text-white',
    {
      // Enter key
      '!bg-[#87CEEB]': isEnterKey && theme === THEMES.colorful,
      'bg-[#4f46e5] text-white text-lg': isEnterKey,

      // Unselected
      '!bg-[#c7e0c4]': !status && !isEnterKey && theme === THEMES.colorful,
      'bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 active:bg-slate-400':
        !status && !isEnterKey,

      // Absent
      '!bg-red-400': status === 'absent' && theme === THEMES.colorful,
      'bg-slate-400 dark:bg-slate-800 text-white': status === 'absent',

      // Present
      '!bg-yellow-400':
        status === 'present' && !isHighContrast && theme === THEMES.colorful,
      'bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700 text-white':
        status === 'present' && !isHighContrast,

      // Correct
      '!bg-green-600':
        status === 'correct' && !isHighContrast && theme === THEMES.colorful,
      'bg-green-500 hover:bg-green-600 active:bg-green-700 text-white':
        status === 'correct' && !isHighContrast,

      // High contrast
      'bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white':
        status === 'correct' && isHighContrast,
      'bg-cyan-500 hover:bg-cyan-600 active:bg-cyan-700 text-white':
        status === 'present' && isHighContrast,
      'transition ease-in-out': isRevealing,
    }
  )

  const styles = {
    transitionDelay: isRevealing ? `${keyDelayMs}ms` : 'unset',
    width: isEnterKey ? `400px` : `${width}px`,
  }

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    onClick(value)
    event.currentTarget.blur()
  }

  return (
    <button
      style={styles}
      aria-label={`${value}${status ? ' ' + status : ''}`}
      className={classes}
      onClick={handleClick}
    >
      {children || value}
    </button>
  )
}
